import {
  CROP_CALENDAR_PLANTING_METHODS,
  CROP_CALENDAR_SEASONS,
  normalizePlantingMethod,
  type CropCalendarFormValues,
} from '../../../config/digisakaApi';

type CropCalendarInputsProps = {
  value: CropCalendarFormValues;
  onChange: (next: CropCalendarFormValues) => void;
  disabled?: boolean;
  inputClass: string;
  labelClass: string;
  selectClass?: string;
  /** Entry form matches crop-calendar mockup: 4 core fields, single column. */
  variant?: 'entry' | 'full';
};

export default function CropCalendarInputs({
  value,
  onChange,
  disabled = false,
  inputClass,
  labelClass,
  selectClass,
  variant = 'entry',
}: CropCalendarInputsProps) {
  const selectCls = selectClass || inputClass;
  const transplanted = normalizePlantingMethod(value.planting_method) === 'Transplanted';
  const requiredMark = <span aria-hidden>*</span>;

  const patch = (partial: Partial<CropCalendarFormValues>) => {
    onChange({ ...value, ...partial });
  };

  if (variant === 'entry') {
    return (
      <div>
        <div>
          <label className={labelClass}>
            Crop type {requiredMark}
          </label>
          <input
            type="text"
            value={value.crop_type}
            onChange={(e) => patch({ crop_type: e.target.value })}
            placeholder="e.g. Rice, Corn"
            disabled={disabled}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>
            Planting method {requiredMark}
          </label>
          <select
            value={value.planting_method}
            onChange={(e) => patch({ planting_method: e.target.value })}
            disabled={disabled}
            className={selectCls}
          >
            <option value="">Choose…</option>
            {CROP_CALENDAR_PLANTING_METHODS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>
            Sowing date {requiredMark}
          </label>
          <input
            type="date"
            value={value.sowing_date}
            onChange={(e) => patch({ sowing_date: e.target.value })}
            disabled={disabled}
            className={inputClass}
          />
        </div>
        {transplanted && (
          <div>
            <label className={labelClass}>
              Planting date {requiredMark}
            </label>
            <input
              type="date"
              value={value.planting_date}
              onChange={(e) => patch({ planting_date: e.target.value })}
              disabled={disabled}
              className={inputClass}
            />
          </div>
        )}
        <div>
          <label className={labelClass}>Expected harvest date</label>
          <input
            type="date"
            value={value.expected_harvest_date}
            onChange={(e) => patch({ expected_harvest_date: e.target.value })}
            disabled={disabled}
            className={inputClass}
            title="Optional — compares with satellite estimate"
          />
          {!value.expected_harvest_date && (
            <p className="mt-1 text-[12px] opacity-70">Optional — compares with satellite estimate</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <label className={labelClass}>
          Crop type {requiredMark}
        </label>
        <input
          type="text"
          value={value.crop_type}
          onChange={(e) => patch({ crop_type: e.target.value })}
          placeholder="e.g. Rice, Corn"
          disabled={disabled}
          className={inputClass}
        />
      </div>
      <div>
        <label className={labelClass}>
          Planting method {requiredMark}
        </label>
        <select
          value={value.planting_method}
          onChange={(e) => patch({ planting_method: e.target.value })}
          disabled={disabled}
          className={selectCls}
        >
          <option value="">Choose…</option>
          {CROP_CALENDAR_PLANTING_METHODS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className={labelClass}>
            Sowing date {requiredMark}
          </label>
          <input
            type="date"
            value={value.sowing_date}
            onChange={(e) => patch({ sowing_date: e.target.value })}
            disabled={disabled}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>
            Planting date {transplanted ? requiredMark : null}
          </label>
          <input
            type="date"
            value={value.planting_date}
            onChange={(e) => patch({ planting_date: e.target.value })}
            disabled={disabled}
            className={inputClass}
          />
          {!transplanted && (
            <p className="mt-1 text-[10px] opacity-70">Needed for transplanted rice; optional for direct seeded.</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className={labelClass}>Expected harvest date</label>
          <input
            type="date"
            value={value.expected_harvest_date}
            onChange={(e) => patch({ expected_harvest_date: e.target.value })}
            disabled={disabled}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Cropping season</label>
          <select
            value={value.cropping_season}
            onChange={(e) => patch({ cropping_season: e.target.value })}
            disabled={disabled}
            className={selectCls}
          >
            <option value="">Select season</option>
            {CROP_CALENDAR_SEASONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
